import React, { useState, useEffect, useRef } from 'react'
import { Label } from '../../../ui_reusable/styles/CreateInputUtils.styles'
import { FormSelectPicker } from '../../../ui_reusable/StandardSelectPickers'
import {
  ModelPickerContainer,
  ModelInfoTitle,
  ModelInfoValue,
} from './styles/BuildScenarioHeader.styles'
import useBuildScenarioStore from '../stores/buildScenarioStore'
import SwitchModelModal from './build_scenario_header/SwitchModelModal'
import {
  WrapWithLineBetween,
  LineCloseToMeElement,
} from '../../../ui_reusable/WrapWithLineBetweenDisplayer'
import { QualityBall } from '../../../comp_reusable/model/QualityTag'
import {
  getFitnessStringByScore,
  getHealthStringByScore,
} from '../../../../utils/ScoreFitnessValidation'
import theme from '../../../../styles/theme'

const BuildScenarioHeader = ({
  selectedClient,
  selectedProject,
  onModelSelect,
  selectedModel: selectedModelProp,
}) => {
  const [models, setModels] = useState([])
  const [selectedModel, setSelectedModel] = useState(selectedModelProp || null)
  const [loading, setLoading] = useState(false)
  const [modalOpen, setModalOpen] = useState(false)
  const [pendingModelId, setPendingModelId] = useState(null)
  const hasRunInitialFetch = useRef(false)

  // Get isReadyToSubmitFlag from store
  const isReadyToSubmitFlag = useBuildScenarioStore(
    (state) => state.isReadyForSubmitFlag,
  )

  // Sync with parent's selected model when it changes (e.g., from store prefill)
  useEffect(() => {
    if (selectedModelProp !== selectedModel) {
      setSelectedModel(selectedModelProp)
    }
  }, [selectedModelProp, selectedModel])

  // Fetch models when client/project changes (matches ModelListPage logic)
  useEffect(() => {
    const fetchModels = async () => {
      setLoading(true)
      try {
        // Full-list model lookup for the dropdown (not a table); bounded
        // server-side at 9999 (BaseCitrusPagination max_page_size).
        let url = `${process.env.REACT_APP_API_URL}/api/v2/artifacts/`
        const params = new URLSearchParams()
        if (selectedClient) {
          params.set('client_id', String(selectedClient.id))
        }
        if (selectedProject) {
          params.set('project_id', String(selectedProject.id))
        }
        const qs = params.toString()
        if (qs) {
          url += `?${qs}`
        }

        const response = await fetch(url, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        })

        if (response.ok) {
          const result = await response.json()
          const newModels = result.data || []
          setModels(newModels)

          // Check if current selected model still exists in new models
          if (selectedModel) {
            const modelStillExists = newModels.some(
              (model) => model.id === selectedModel.id,
            )
            if (!modelStillExists) {
              // Clear the selected model if it's not in the new list
              setSelectedModel(null)
              if (onModelSelect) {
                onModelSelect(null)
              }
            }
          }
        }
      } catch (error) {
        console.error('Error fetching models:', error)
      } finally {
        setLoading(false)
        hasRunInitialFetch.current = true
      }
    }

    // Only run the fetch after contexts have had time to initialize or when they change
    if (!hasRunInitialFetch.current) {
      // First load: wait a bit for contexts to load from localStorage
      const timer = setTimeout(() => {
        fetchModels()
      }, 100)
      return () => clearTimeout(timer)
    } else {
      // Subsequent changes: run immediately
      fetchModels()
    }
  }, [selectedClient, selectedProject, selectedModel, onModelSelect])

  const handleModelChange = (modelId) => {
    // If isReadyToSubmitFlag is true, show confirmation modal
    if (isReadyToSubmitFlag) {
      setPendingModelId(modelId)
      setModalOpen(true)
      return
    }

    // Otherwise, proceed directly
    proceedWithModelChange(modelId)
  }

  const proceedWithModelChange = (modelId) => {
    const model = models.find((m) => m.id === modelId)
    setSelectedModel(model)
    if (onModelSelect) {
      onModelSelect(model)
    }
  }

  const handleModalConfirm = () => {
    if (pendingModelId !== null) {
      proceedWithModelChange(pendingModelId)
    }
    setModalOpen(false)
    setPendingModelId(null)
  }

  const handleModalClose = () => {
    setModalOpen(false)
    setPendingModelId(null)
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    return new Date(dateString).toISOString().slice(0, 10)
  }

  const formatTimeframe = (timeframe) => {
    if (!timeframe || !timeframe.from || !timeframe.to) return 'N/A'
    return (
      <div
        style={{ display: 'flex', flexDirection: 'column', fontSize: '0.6rem' }}
      >
        <span>{formatDate(timeframe.from)}</span>
        <span>to {formatDate(timeframe.to)}</span>
      </div>
    )
  }

  return (
    <>
      <div>
        <ModelPickerContainer>
          <Label>Select Model</Label>
          <FormSelectPicker
            placeholder="Choose a model..."
            searchable={true}
            cleanable={false}
            loading={loading}
            value={selectedModel?.id || null}
            data={models.map((model) => ({
              label: model.name,
              value: model.id,
            }))}
            onChange={handleModelChange}
            style={{ width: '25rem' }}
          />
        </ModelPickerContainer>

        {selectedModel && (
          <WrapWithLineBetween
            style={{ backgroundColor: theme.colors.surface }}
          >
            <LineCloseToMeElement style={{ width: '12rem' }}>
              <ModelInfoTitle>Outcome variable</ModelInfoTitle>
              <ModelInfoValue>
                {selectedModel.dependent_driver?.name || 'N/A'}
              </ModelInfoValue>
            </LineCloseToMeElement>

            <LineCloseToMeElement style={{ width: '10rem' }}>
              <ModelInfoTitle>Run date</ModelInfoTitle>
              <ModelInfoValue>
                {formatDate(selectedModel.run_at)}
              </ModelInfoValue>
            </LineCloseToMeElement>

            <LineCloseToMeElement style={{ width: '10rem' }}>
              <ModelInfoTitle>Model fit score</ModelInfoTitle>
              <ModelInfoValue
                style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
              >
                <span>
                  {selectedModel.fit_score
                    ? Math.round(selectedModel.fit_score)
                    : 'N/A'}
                </span>
                {selectedModel.fit_score && (
                  <QualityBall
                    size={'xs'}
                    quality={getFitnessStringByScore(selectedModel.fit_score)}
                  />
                )}
              </ModelInfoValue>
            </LineCloseToMeElement>

            <LineCloseToMeElement style={{ width: '10rem' }}>
              <ModelInfoTitle>Model health score</ModelInfoTitle>
              <ModelInfoValue
                style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
              >
                <span>
                  {selectedModel.health_score
                    ? Math.round(selectedModel.health_score)
                    : 'N/A'}
                </span>
                {selectedModel.health_score && (
                  <QualityBall
                    size={'xs'}
                    quality={getHealthStringByScore(
                      selectedModel.health_score * 10,
                    )}
                  />
                )}
              </ModelInfoValue>
            </LineCloseToMeElement>

            <LineCloseToMeElement style={{ width: '10rem' }}>
              <ModelInfoTitle>Timeframe</ModelInfoTitle>
              <ModelInfoValue style={{ maxHeight: '1.3rem' }}>
                {formatTimeframe(selectedModel.timeframe)}
              </ModelInfoValue>
            </LineCloseToMeElement>

            <LineCloseToMeElement style={{ width: '10rem' }}>
              <ModelInfoTitle>Frequency</ModelInfoTitle>
              <ModelInfoValue>
                {selectedModel.parameters?.model_data_frequency || 'N/A'}
              </ModelInfoValue>
            </LineCloseToMeElement>
          </WrapWithLineBetween>
        )}
      </div>

      <SwitchModelModal
        open={modalOpen}
        onClose={handleModalClose}
        onConfirm={handleModalConfirm}
      />
    </>
  )
}

export default BuildScenarioHeader
